import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import services from "../../../services";
import { useForm, useWatch } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import BarLoading from "../../../components/Loading/BarLoading";
import { yupResolver } from "@hookform/resolvers/yup";
import { NameAndMainCategory } from "../validations";
import { Switch, rem } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { IconX } from "@tabler/icons-react";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import SaveButton from "../../../components/Buttons/SaveButton";
import CustomInput from "../../../components/CustomInput/CustomInput";

const ProductSubCategoryCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let { id } = useParams();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    getValues,
    control,

    formState: { errors },
  } = useForm({
    resolver: yupResolver(NameAndMainCategory),
    defaultValues: {
      name: "",
      parent_id: null,
      is_active: true,
    },
  });

  const addProductCategory = useMutation({
    mutationFn: (newProductCategory) =>
      services.productSubCategoryCreate(newProductCategory),
  });
  const updateProductCategory = useMutation({
    mutationFn: (newProductCategory) =>
      services.productSubCategoryUpdate(id, newProductCategory),
  });

  const onSubmit = async (data) => {
    let newProductSubCategoryPayload;
    let productSubCategory;

    try {
      newProductSubCategoryPayload = {
        ...data,
      };
      if (!id) {
        productSubCategory = await addProductCategory.mutateAsync(
          newProductSubCategoryPayload
        );
      } else {
        productSubCategory = await updateProductCategory.mutateAsync(
          newProductSubCategoryPayload
        );
      }

      if (!id && productSubCategory) {
        toast.success(
          productSubCategory?.data?.message
            ? productSubCategory?.data?.message
            : t("notifications.success.product-sub-category-create"),
          {
            duration: 3000,
          }
        );

        reset();
        handleCancel();
      } else if (id && productSubCategory) {
        toast.success(
          productSubCategory?.data?.message
            ? productSubCategory?.data?.message
            : t("notifications.success.product-sub-category-update"),
          {
            duration: 3000,
          }
        );
        handleCancel();
      }
    } catch (e) {
      toast.error(
        productSubCategory?.data?.message
          ? productSubCategory?.data?.message
          : t("notifications.error.message"),
        {
          duration: 3000,
        }
      );
    }
  };

  const handleCancel = () => {
    reset();
    navigate("/product/sub-category/list");
  };

  const {
    isLoading: categoryLoading,
    error: categoryError,
    data: categoryData,
    isSuccess: categorySuccess,
    refetch: categoryRefetch,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: async () =>
      await services.productCategoryList(1, 100).then((res) => [
        ...res.data.data.map((item) => ({
          value: item._id,
          label: item.name,
        })),
      ]),
  });

  const fetchProductCategory = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const productSubCategory = await services.getProductSubCategoryById(id);
        Object.keys(productSubCategory.data).forEach((key) => {
          setValue(key, productSubCategory.data[key]);
        });
      } catch (e) {
        navigate("/product/sub-category/list");
        toast.error(t("notifications.error.product-category-notfound"), {
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    reset();
    categoryRefetch();
    if (id) {
      fetchProductCategory();
    }
  }, [id]);

  const isActive = useWatch({
    control,
    name: "is_active",
    defaultValue: true,
  });
  return (
    <>
      {!loading && !categoryLoading ? (
        <div className="px-4 sm:px-6 lg:px-8">
          <form
            onSubmit={handleSubmit((data) =>
              onSubmit(data, getValues("photo"))
            )}
          >
            <div className="space-y-12">
              <div className="border-b border-white/10 pb-12">
                <h2 className="text-base font-semibold leading-7 dark:text-white text-slate-700">
                  {id
                    ? t("titles.product-sub-category-edit")
                    : t("titles.product-sub-category-create")}
                </h2>
                <p className="mt-1 text-sm leading-6 text-gray-400">
                  {id
                    ? t("descriptions.product-sub-category-edit")
                    : t("descriptions.product-sub-category-create")}
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 max-w-full">
                  <div className="sm:col-span-full ">
                    {!categoryError && !categoryLoading && categoryData && (
                      <div className="w-[100%] flex flex-col">
                        <label
                          htmlFor="category"
                          className="block text-sm font-medium leading-6 dark:text-white text-slate-700"
                        >
                          {t("product.main-category")}
                        </label>
                        <div className="mt-2">
                          <CustomSelect
                            control={control}
                            placeholder={t("placeholders.select")}
                            name="parent_id"
                            error={errors.parent_id?.message}
                            data={categoryData}
                            searchable
                            height={"20px"}
                          />
                          <p
                            className="mt-2 text-sm text-red-600"
                            id="email-error"
                          >
                            {t(errors.parent_id?.message)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-full">
                    <CustomInput
                      label={t("product.category-name")}
                      register={register}
                      name="name"
                      errors={errors}
                      placeholder={t("product.category-name")}
                    />
                  </div>

                  <div className="sm:col-span-full">
                    <label
                      htmlFor="first-name"
                      className="block text-sm font-medium leading-6 dark:text-white text-slate-700"
                    >
                      {t("labels.is-active")}
                    </label>
                    <div className="mt-2">
                      <Switch
                        checked={isActive}
                        onChange={(event) =>
                          setValue("is_active", event.target.checked)
                        }
                        color="teal"
                        size="lg"
                        thumbIcon={
                          isActive ? (
                            <IconCheck
                              style={{ width: rem(12), height: rem(12) }}
                              color="green"
                              stroke={3}
                            />
                          ) : (
                            <IconX
                              style={{ width: rem(12), height: rem(12) }}
                              color="red"
                              stroke={3}
                            />
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-x-6">
              <button
                type="button"
                onClick={handleCancel}
                className="text-sm font-semibold leading-6 dark:text-white text-slate-700"
              >
                {t("actions.cancel")}
              </button>
              <SaveButton
                disabled={
                  id
                    ? updateProductCategory.isLoading
                    : addProductCategory.isLoading
                }
              />
            </div>
          </form>
        </div>
      ) : (
        <BarLoading />
      )}
    </>
  );
};

export default ProductSubCategoryCreate;
